<template>
  <label
    class="flex items-center p-3 border rounded-lg cursor-pointer transition-colors"
    :class="[
      isSelected
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/40'
        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
      isPlRecommended && !isSelected ? 'ring-2 ring-green-200 dark:ring-green-700' : ''
    ]"
  >
    <input
      type="radio"
      :name="inputName"
      :value="employeeId"
      :checked="isSelected"
      @change="$emit('select', employeeId)"
      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
    />
    <div class="ml-3 flex-1">
      <div class="flex items-center gap-2">
        <span class="font-medium text-gray-900 dark:text-white">{{ employeeName }}</span>
        <!-- PL Recommendation Badge -->
        <span
          v-if="isPlRecommended"
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
        >
          Aanbevolen door PL
        </span>
        <!-- Not yet started warning -->
        <span
          v-if="isNotYetStarted"
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300"
          :title="`In dienst vanaf ${formatContractEndDate(contractStartDate)}`"
        >
          Nog niet in dienst
        </span>
        <!-- Contract ends before project warning -->
        <span
          v-if="extendsPassContract"
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300"
          :title="`Contract eindigt ${formatContractEndDate(contractEndDate)}, voor einde project`"
        >
          Contract loopt af
        </span>
      </div>
      <div
        v-if="employeeFunction || employeeTeams"
        class="text-xs text-gray-500 dark:text-gray-400"
      >
        {{ [employeeFunction, employeeTeams].filter(Boolean).join(' · ') }}
      </div>
    </div>

    <!-- Match stats -->
    <div v-if="matchStats" class="flex items-center gap-3 ml-2">
      <div class="text-center">
        <div class="text-sm font-semibold px-2 py-0.5 rounded" :class="[matchColor, matchBgColor]">
          {{ matchStats.matchPercent }}%
        </div>
        <div class="text-xs text-gray-400 dark:text-gray-500">match</div>
      </div>
      <div class="text-center">
        <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ matchStats.avgAvailableDays }}d
        </div>
        <div class="text-xs text-gray-400 dark:text-gray-500">gem/week</div>
      </div>
    </div>

    <!-- Multiple proposals warning -->
    <div v-if="otherProposals.length > 0" class="ml-2">
      <div
        ref="triggerRef"
        @mouseenter="onTriggerEnter"
        @mouseleave="onTriggerLeave"
        class="flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded text-xs font-medium cursor-help"
      >
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        +{{ otherProposals.length }}
      </div>
      <!-- Tooltip rendered into body so an ancestor's overflow-hidden can't clip it -->
      <Teleport to="body">
        <div
          v-if="showTooltip"
          class="fixed z-[9999] -translate-x-1/2 -translate-y-full pointer-events-none"
          :style="{ top: tooltipPos.top + 'px', left: tooltipPos.left + 'px' }"
        >
          <div class="bg-gray-900 text-white text-xs rounded-lg p-3 whitespace-nowrap shadow-lg">
            <div class="font-semibold mb-1">Ook voorgedragen voor:</div>
            <ul class="space-y-1">
              <li v-for="(other, idx) in otherProposals" :key="idx">
                {{ other.organizationName }} - {{ other.role }} ({{ other.dpw }}d/w)
              </li>
            </ul>
          </div>
          <div
            class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"
          ></div>
        </div>
      </Teleport>
    </div>
  </label>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useContractStatus } from '../../composables/domain/useContractStatus.js'
import { useAvailability } from '../../composables/data/useAvailability.js'

const { formatContractEndDate, projectExtendsPastContract, getContractStatus, STATUS } =
  useContractStatus()

const props = defineProps({
  employeeId: {
    type: String,
    required: true
  },
  employeeName: {
    type: String,
    required: true
  },
  employeeFunction: {
    type: String,
    default: ''
  },
  employeeTeams: {
    type: String,
    default: ''
  },
  inputName: {
    type: String,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isPlRecommended: {
    type: Boolean,
    default: false
  },
  matchStats: {
    type: Object,
    default: null
  },
  otherProposals: {
    type: Array,
    default: () => []
  },
  contractEndDate: {
    type: [String, Date],
    default: null
  },
  contractStartDate: {
    type: [String, Date],
    default: null
  },
  projectEndDate: {
    type: [String, Date],
    default: null
  }
})

// Contract status computed properties
const extendsPassContract = computed(() =>
  projectExtendsPastContract(props.projectEndDate, props.contractEndDate)
)
const isNotYetStarted = computed(
  () => getContractStatus(props.contractEndDate, props.contractStartDate) === STATUS.NOT_YET_STARTED
)

defineEmits(['select'])

const { getMatchColor, getMatchBgColor } = useAvailability()

// Match color helpers (using shared utility with dark mode support)
const matchColor = computed(() => {
  if (!props.matchStats) return ''
  return getMatchColor(props.matchStats.matchPercent)
})

const matchBgColor = computed(() => {
  if (!props.matchStats) return ''
  return getMatchBgColor(props.matchStats.matchPercent)
})

// Tooltip positioning: rendered via Teleport so it isn't clipped by an
// overflow-hidden ancestor (SourcingProspectCard).
const triggerRef = ref(null)
const showTooltip = ref(false)
const tooltipPos = ref({ top: 0, left: 0 })

function onTriggerEnter() {
  if (!triggerRef.value) return
  const rect = triggerRef.value.getBoundingClientRect()
  tooltipPos.value = {
    top: rect.top - 8,
    left: rect.left + rect.width / 2
  }
  showTooltip.value = true
}

function onTriggerLeave() {
  showTooltip.value = false
}
</script>
